import React, { useState, useEffect } from 'react'
import { FiUser, FiPackage, FiCalendar, FiMapPin, FiMail, FiEdit2, FiTrash2, FiPlus, FiCheck, FiClock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import useAuthStore from '../stores/authStore'
import {
    getProfile,
    updateProfile,
    getProfileOrders,
    getUpcomingEvents,
    getAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    getNewsletters,
    subscribeNewsletter,
    unsubscribeNewsletter
} from '../services/api'

// Error Boundary to catch rendering errors
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        console.error('Profile Error:', error, errorInfo)
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="card p-6 bg-red-900/20 border-red-500">
                    <h3 className="text-red-400 font-bold mb-2">{window.t('vaznikna_greshka')}</h3>
                    <p className="text-gray-400 text-sm mb-4">{this.state.error?.message || window.t('vaznikna_greshka_pri_zarezhdane_na_tazi_sektsiya')}</p>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null })}
                        className="btn-outline text-sm"
                    >
                        {window.t('opitayte_otnovo')}
                    </button>
                </div>
            )
        }
        return this.props.children
    }
}

export default function ProfilePage() {
    const { user } = useAuthStore()
    const [activeTab, setActiveTab] = useState('overview')
    const [loading, setLoading] = useState(true)
    const [profile, setProfile] = useState(null)
    const [stats, setStats] = useState({})
    const [orders, setOrders] = useState([])
    const [upcomingEvents, setUpcomingEvents] = useState([])
    const [addresses, setAddresses] = useState([])
    const [newsletters, setNewsletters] = useState([])
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [editingAddress, setEditingAddress] = useState(null)
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadProfile()
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)

        if (activeTab === 'orders') loadOrders()
        if (activeTab === 'upcoming') loadUpcomingEvents()
        if (activeTab === 'addresses') loadAddresses()
        if (activeTab === 'newsletters') loadNewsletters()
    }, [activeTab, page])

    const loadProfile = async () => {
        try {
            const res = await getProfile()
            setProfile(res.data.user)
            setStats(res.data.stats)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const loadOrders = async () => {
        try {
            const res = await getProfileOrders({ page: page })

            const data = res.data

            setOrders(data.data || [])
            setLastPage(data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const loadUpcomingEvents = async () => {
        try {
            const res = await getUpcomingEvents()
            setUpcomingEvents(res.data.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const loadAddresses = async () => {
        try {
            const res = await getAddresses()
            setAddresses(res.data.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const loadNewsletters = async () => {
        try {
            const res = await getNewsletters()
            setNewsletters(res.data.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const handleNewsletterToggle = async (newsletter) => {
        try {
            if (newsletter.is_subscribed) {
                await unsubscribeNewsletter(newsletter.slug)
                toast.success(`${window.t('otpisahte_se_ot')} ${newsletter.name}`)
            } else {
                await subscribeNewsletter(newsletter.slug)
                toast.success(`${window.t('abonirahte_se_za')} ${newsletter.name}`)
            }
            loadNewsletters()
        } catch (error) {
            toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
        }
    }

    const handleDeleteAddress = async (id) => {
        if (!confirm(`${window.t('iztrivane_na_tozi_adres')}`)) return
        try {
            await deleteAddress(id)
            toast.success(window.t('adresat_e_iztrit'))
            loadAddresses()
        } catch (error) {
            toast.error(window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    const handleSetDefault = async (id) => {
        try {
            await setDefaultAddress(id)
            toast.success(window.t('adresat_po_podrazbirane_e_aktualiziran'))
            loadAddresses()
        } catch (error) {
            toast.error(window.t('greshka_pri_zadavane_na_adres_po_podrazbirane'))
        }
    }

    const tabs = [
        { id: 'overview', label: `${window.t('obsht_pregled')}`, icon: FiUser },
        { id: 'orders', label: `${window.t('istoriya_na_porachkite')}`, icon: FiPackage },
        { id: 'upcoming', label: `${window.t('predstoyashti_sabitiya')}`, icon: FiCalendar },
        { id: 'addresses', label: `${window.t('adresi')}`, icon: FiMapPin },
        { id: 'newsletters', label: `${window.t('byuletini')}`, icon: FiMail },
    ]

    if (loading) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    return (
        <div className="min-h-screen pt-16 pb-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white">{window.t('moyat_profil')}</h1>
                    <p className="text-gray-400 mt-1">{window.t('upravlyavayte_vashiya_profil_p')}</p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-800 pb-4">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 pt-[0.4rem] pb-[0.5rem] rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                                ? 'bg-primary-500 text-gray-900'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <ErrorBoundary>
                    {activeTab === 'overview' && (
                        <OverviewTab profile={profile} stats={stats} onUpdate={loadProfile} />
                    )}
                    {activeTab === 'orders' && (
                        <OrdersTab orders={orders} page={page} setPage={setPage} lastPage={lastPage} />
                    )}
                    {activeTab === 'upcoming' && (
                        <UpcomingEventsTab events={upcomingEvents} />
                    )}
                    {activeTab === 'addresses' && (
                        <AddressesTab
                            addresses={addresses}
                            onDelete={handleDeleteAddress}
                            onSetDefault={handleSetDefault}
                            onReload={loadAddresses}
                        />
                    )}
                    {activeTab === 'newsletters' && (
                        <NewslettersTab newsletters={newsletters} onToggle={handleNewsletterToggle} />
                    )}
                </ErrorBoundary>
            </div>
        </div>
    )
}

// Overview Tab
function OverviewTab({ profile, stats, onUpdate }) {
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [saving, setSaving] = useState(false)

    // Update form state when profile loads or changes
    useEffect(() => {
        if (profile) {
            setName(profile.name || '')
            setPhone(profile.phone || '')
        }
    }, [profile])

    const handleUpdateProfile = async (e) => {
        e.preventDefault()

        if (phone && !/^(\+359|0)\s?[1-9](\s?[0-9]){7,8}$/.test(phone)) {
            toast.error(window.t('molya_vavedete_validen_balgarski_telefonen_nomer'))
            return
        }

        setSaving(true)
        try {
            await updateProfile({ name, phone })
            toast.success(window.t('profilat_e_aktualiziran'))
            setEditing(false)
            onUpdate()
        } catch (error) {
            toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
        } finally {
            setSaving(false)
        }
    }

    // Return loading state if profile hasn't loaded yet
    if (!profile) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    return (
        <div className="grid md:grid-cols-2 gap-6">
            {/* Profile Card */}
            <div className="card p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">{window.t('informatsiya_za_profila')}</h2>
                    {!editing && (
                        <button onClick={() => setEditing(true)} className="btn-outline px-3 py-1.5 text-sm">
                            <FiEdit2 className="w-4 h-4 mr-1" />
                            {window.t('redaktirane')}
                        </button>
                    )}
                </div>

                {editing ? (
                    <div className="space-y-4">
                        <div>
                            <label className="label">{window.t('ime')}</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="label">{window.t('imeyl')}</label>
                            <input
                                type="email"
                                value={profile?.email}
                                disabled
                                className="input bg-gray-800 cursor-not-allowed"
                            />
                        </div>
                        <div>
                            <label className="label">{window.t('telefon')}</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="input"
                                placeholder="+359 88 123 4567"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button onClick={handleUpdateProfile} className="btn-primary" disabled={saving}>
                                {saving ? window.t('zapazvane') : window.t('zapazvane_na_promenite')}
                            </button>
                            <button onClick={() => setEditing(false)} className="btn-outline">
                                {window.t('otkaz')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <div className="text-gray-400 text-sm">{window.t('ime')}</div>
                            <div className="text-white font-medium">{profile?.name}</div>
                        </div>
                        <div>
                            <div className="text-gray-400 text-sm">{window.t('imeyl')}</div>
                            <div className="text-white font-medium">{profile?.email}</div>
                        </div>
                        <div>
                            <div className="text-gray-400 text-sm">{window.t('telefon')}</div>
                            <div className="text-white font-medium">{profile?.phone || '—'}</div>
                        </div>
                        <div>
                            <div className="text-gray-400 text-sm">{window.t('chlen_ot')}</div>
                            <div className="text-white font-medium">
                                {profile?.created_at
                                    ? new Date(profile.created_at).toLocaleDateString('bg-BG', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })
                                    : '—'
                                }
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Stats Card */}
            <div className="card p-6">
                <h2 className="text-xl font-bold text-white mb-6">{window.t('barza_statistika')}</h2>
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-primary-400">{stats?.total_orders || 0}</div>
                        <div className="text-gray-400 text-sm">{window.t('obshto_porachki')}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-accent-400">€{parseFloat(stats?.total_spent || 0).toFixed(2)}</div>
                        <div className="text-gray-400 text-sm">{window.t('obshto_poharcheni')}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-green-400">{stats?.upcoming_events || 0}</div>
                        <div className="text-gray-400 text-sm">{window.t('predstoyashti_sabitiya')}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-blue-400">{stats?.total_tickets || 0}</div>
                        <div className="text-gray-400 text-sm">{window.t('obshto_bileti')}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// Orders Tab
function OrdersTab({ orders, page, setPage, lastPage }) {

    const currentLanguage = localStorage.getItem('app_lang')

    const getStatusText = (status) => {
        switch (status) {
            case 'paid': return `${window.t('platen')}`
            case 'completed': return `${window.t('platen')}`
            case 'pending': return `${window.t('izchakva')}`
            case 'cancelled': return `${window.t('otkazan')}`
            default: return status
        }
    }

    if (orders.length === 0) {
        return (
            <div className="card p-12 text-center">
                <FiPackage className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400 text-lg mb-4">{window.t('vse_oshte_nyama_porachki')}</p>
                <a href="/" className="btn-primary">{window.t('razgledayte_sabitiyata')}</a>
            </div>
        )
    }

    return (
        <>
            <div className="space-y-4">
                {orders.map(order => (
                    <div key={order.id} className="card p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-lg font-bold text-white">
                                        {order.performance?.event?.title}
                                    </h3>
                                    <span className={`px-2 py-1 rounded-full text-xs ${order.status === 'paid' ? 'bg-green-500/20 text-green-800' :
                                        order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-800' :
                                            'bg-red-500/20 text-red-800'
                                        }`}>
                                        {getStatusText(order.status)}
                                    </span>
                                </div>
                                <div className="text-gray-400 text-sm">
                                    {window.t('porachka')} #{order.id} •
                                    {currentLanguage === 'en'
                                        ? new Date(order.created_at).toLocaleDateString('en-EN')
                                        : new Date(order.created_at).toLocaleDateString('bg-BG')
                                    }
                                </div>
                                <div className="text-gray-400 text-sm mt-1">
                                    {order.tickets?.length} {window.t('bilet_a')}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-white">€{order.total_amount}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 text-center">
                <select
                    value={page}
                    onChange={(e) => setPage(Number(e.target.value))}
                    className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                >
                    {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>
            </div>
        </>
    )
}

// Upcoming Events Tab
function UpcomingEventsTab({ events }) {

    const currentLanguage = localStorage.getItem('app_lang')

    if (events.length === 0) {
        return (
            <div className="card p-12 text-center">
                <FiCalendar className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400 text-lg mb-4">{window.t('nyama_predstoyashti_sabitiya')}</p>
                <a href="/" className="btn-primary">{window.t('razgledayte_sabitiyata')}</a>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {events.map((event, index) => (
                <div key={index} className="card p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-white mb-2">
                                {event.event?.title}
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                <span className="flex items-center gap-1">
                                    <FiCalendar />
                                    {currentLanguage === 'en'
                                        ? new Date(event.performance?.start_time).toLocaleDateString('en-EN')
                                        : new Date(event.performance?.start_time).toLocaleDateString('bg-BG')
                                    }
                                </span>
                                <span className="flex items-center gap-1">
                                    <FiClock />
                                    {currentLanguage === 'en'
                                        ? new Date(event.performance?.start_time).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        })
                                        : new Date(event.performance?.start_time).toLocaleTimeString('bg-BG', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false
                                        })
                                    }
                                </span>
                                <span className="flex items-center gap-1">
                                    <FiMapPin />
                                    {event.hall?.name}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-500 text-sm">
                                {event.ticket_count} {window.t('bilet_a')}
                            </span>
                            <a href="/my-tickets" className="btn-outline px-4 py-2 text-sm">
                                {window.t('vizh_biletite')}
                            </a>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

// Addresses Tab
function AddressesTab({ addresses, onDelete, onSetDefault, onReload }) {
    const [showForm, setShowForm] = useState(false)
    const [editingId, setEditingId] = useState(null)

    const handleSave = () => {
        setShowForm(false)
        setEditingId(null)
        onReload()
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">{window.t('vashite_adresi')}</h2>
                <button onClick={() => setShowForm(true)} className="btn-primary">
                    <FiPlus className="w-4 h-4 mr-2" />
                    {window.t('dobavyane_na_adres')}
                </button>
            </div>

            {showForm && (
                <AddressForm onSave={handleSave} onCancel={() => setShowForm(false)} />
            )}

            {addresses.length === 0 && !showForm ? (
                <div className="card p-12 text-center">
                    <FiMapPin className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400 text-lg">{window.t('nyama_zapazeni_adresi')}</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {addresses.map(address => (
                        <div key={address.id} className={`card p-6 ${address.is_default ? 'border-primary-500' : ''}`}>
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${address.type === 'delivery' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                                        }`}>
                                        {address.type}
                                    </span>
                                    {address.is_default && (
                                        <span className="ml-2 px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">
                                            {window.t('po_podrazbirane')}
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    {!address.is_default && (
                                        <button onClick={() => onSetDefault(address.id)} className="text-gray-400 hover:text-white">
                                            <FiCheck className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button onClick={() => onDelete(address.id)} className="text-gray-400 hover:text-red-400">
                                        <FiTrash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="text-white font-medium">{address.name}</div>
                            <div className="text-gray-400 text-sm mt-1">
                                {address.street}
                                {address.street2 && <>, {address.street2}</>}
                            </div>
                            <div className="text-gray-400 text-sm">
                                {address.city}, {address.state} {address.postal_code}
                            </div>
                            <div className="text-gray-400 text-sm">{address.country}</div>
                            {address.phone && <div className="text-gray-400 text-sm mt-2">{address.phone}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// Address Form
function AddressForm({ address = null, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        type: address?.type || 'delivery',
        name: address?.name || '',
        street: address?.street || '',
        street2: address?.street2 || '',
        city: address?.city || '',
        state: address?.state || '',
        postal_code: address?.postal_code || '',
        country: address?.country || 'BG',
        phone: address?.phone || '',
        is_default: address?.is_default || false,
    })
    const [saving, setSaving] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            if (address) {
                await updateAddress(address.id, formData)
            } else {
                await createAddress(formData)
            }
            toast.success(address ? window.t('adresat_e_aktualiziran') : window.t('adresat_e_dobaven'))
            onSave()
        } catch (error) {
            const errorMsg = error.response?.data?.message || window.t('greshka_pri_zapazvane_na_adresa')
            toast.error(errorMsg)
        } finally {
            setSaving(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="card p-6 mb-6">
            <h3 className="text-lg font-bold text-white mb-4">{address ? window.t('redaktirane_na_adres') : window.t('nov_adres')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="label">{window.t('tip')}</label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="input"
                    >
                        <option value="delivery">{window.t('dostavka')}</option>
                        <option value="billing">{window.t('plashtane')}</option>
                    </select>
                </div>
                <div>
                    <label className="label">{window.t('ime_na_poluchatel')}</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input"
                        required
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="label">{window.t('adres')}</label>
                    <input
                        type="text"
                        value={formData.street}
                        onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                        className="input"
                        required
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="label">{window.t('ap_etazh_i_dr_nezadalzhitelno')}</label>
                    <input
                        type="text"
                        value={formData.street2}
                        onChange={(e) => setFormData({ ...formData, street2: e.target.value })}
                        className="input"
                    />
                </div>
                <div>
                    <label className="label">{window.t('grad')}</label>
                    <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="input"
                        required
                    />
                </div>
                <div>
                    <label className="label">{window.t('oblast')}</label>
                    <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="input"
                    />
                </div>
                <div>
                    <label className="label">{window.t('poshtenski_kod')}</label>
                    <input
                        type="text"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        className="input"
                        required
                    />
                </div>
                <div>
                    <label className="label">{window.t('darzhava')}</label>
                    <select
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="input"
                        required
                    >
                        <option value="BG">{window.t('balgariya')}</option>
                        <option value="RO">{window.t('rumaniya')}</option>
                        <option value="GR">{window.t('gartsiya')}</option>
                        <option value="TR">{window.t('turtsiya')}</option>
                        <option value="MK">{window.t('severna_makedoniya')}</option>
                        <option value="RS">{window.t('sarbiya')}</option>
                        <option value="DE">{window.t('germaniya')}</option>
                        <option value="GB">{window.t('velikobritaniya')}</option>
                        <option value="US">{window.t('sasht')}</option>
                    </select>
                </div>
                <div>
                    <label className="label">{window.t('telefon_nezadalzhitelno')}</label>
                    <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="is_default"
                        checked={formData.is_default}
                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-primary-500 focus:ring-primary-500"
                    />
                    <label htmlFor="is_default" className="text-gray-300">{window.t('zaday_po_podrazbirane')}</label>
                </div>
            </div>
            <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? window.t('zapazvane') : window.t('zapazvane_na_adresa')}
                </button>
                <button type="button" onClick={onCancel} className="btn-outline">
                    {window.t('otkaz')}
                </button>
            </div>
        </form>
    )
}

// Newsletters Tab
function NewslettersTab({ newsletters, onToggle }) {

    const currentLanguage = localStorage.getItem('app_lang')

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-bold text-white">{window.t('abonamenti_za_byuletini')}</h2>
                <p className="text-gray-400 mt-1">{window.t('izberete_koi_imeyli_iskate_da_')}</p>
            </div>

            <div className="space-y-4">
                {newsletters.map(newsletter => (
                    <div key={newsletter.id} className="card p-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-white font-medium">
                                {currentLanguage === 'en' && newsletter.name_en
                                    ? newsletter.name_en
                                    : newsletter.name
                                }
                            </h3>
                            <p className="text-gray-400 text-sm mt-1">
                                {currentLanguage === 'en' && newsletter.description_en
                                    ? newsletter.description_en
                                    : newsletter.description
                                }
                            </p>
                        </div>
                        <button
                            onClick={() => onToggle(newsletter)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${newsletter.is_subscribed
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                        >
                            {newsletter.is_subscribed ? window.t('aboniran') : window.t('abonirane')}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
}
